import React from 'react';
import type { BoardFloorPlanData } from '../../../types';
import FloorPlanDrawing from './FloorPlanDrawing';
import type { FloorPlanPdfLayout } from './floorPlanPdf';

interface FloorPlanPrintSheetProps {
  data: BoardFloorPlanData;
  layout: FloorPlanPdfLayout;
  dateLabel: string;
}

const TITLE_FONT = 'Helvetica, Arial, sans-serif';

const FloorPlanPrintSheet: React.FC<FloorPlanPrintSheetProps> = ({ data, layout, dateLabel }) => {
  const title = (data.title || '').trim() || 'Floor plan';
  const dpi = layout.widthPx / layout.pageW;
  const pad = layout.margin * dpi;
  const drawPxW = layout.drawW * dpi;
  const drawPxH = layout.drawH * dpi;
  const titleH = layout.titlePx;
  const fontSize = Math.max(15, titleH * 0.26);
  const titleY = pad + drawPxH + titleH * 0.64;
  const innerLeft = pad + 16;
  const innerRight = layout.widthPx - pad - 16;
  const midX = layout.widthPx / 2;
  const { viewBox } = layout;

  return (
    <svg
      className="fp-print-sheet"
      xmlns="http://www.w3.org/2000/svg"
      width={layout.widthPx}
      height={layout.heightPx}
      viewBox={`0 0 ${layout.widthPx} ${layout.heightPx}`}
    >
      <rect width={layout.widthPx} height={layout.heightPx} fill="#ffffff" />
      <rect
        x={pad}
        y={pad}
        width={layout.widthPx - pad * 2}
        height={layout.heightPx - pad * 2}
        fill="#ffffff"
        stroke="#1e293b"
        strokeWidth={2}
      />
      <line
        x1={pad}
        y1={pad + drawPxH}
        x2={layout.widthPx - pad}
        y2={pad + drawPxH}
        stroke="#1e293b"
        strokeWidth={2}
      />
      <svg
        x={pad}
        y={pad}
        width={drawPxW}
        height={drawPxH}
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <rect x={viewBox.x} y={viewBox.y} width={viewBox.w} height={viewBox.h} fill="#ffffff" />
        <FloorPlanDrawing
          data={{ ...data, underlays: [] }}
          showGrid
          showDimensions
          showHandles={false}
          viewBox={viewBox}
          pxPerIn={layout.pxPerIn}
          print
        />
      </svg>
      <text
        x={innerLeft}
        y={titleY}
        textAnchor="start"
        fontFamily={TITLE_FONT}
        fontSize={fontSize}
        fontWeight={700}
        fill="#1e293b"
        xmlSpace="preserve"
      >
        {"Leiper's Creek Timber Frames"}
      </text>
      <text
        x={midX}
        y={titleY}
        textAnchor="middle"
        fontFamily={TITLE_FONT}
        fontSize={fontSize}
        fontWeight={700}
        fill="#1e293b"
        xmlSpace="preserve"
      >
        {title}
      </text>
      <text
        x={innerRight}
        y={titleY}
        textAnchor="end"
        fontFamily={TITLE_FONT}
        fontSize={fontSize}
        fontWeight={600}
        fill="#334155"
        xmlSpace="preserve"
      >
        <tspan>{layout.scaleLabel}</tspan>
        <tspan dx={18}>{dateLabel}</tspan>
      </text>
    </svg>
  );
};

export default FloorPlanPrintSheet;
